/**
 * Abschnitt "Wissen" auf Projekt- und Ziel-Detailseite und an den Bereichen
 * (F-19): die verknuepften Quellen mit Typ, Status und Fortschritt - und die
 * Verknuepfung von dieser Seite aus (Leitlinie 5: von beiden Seiten).
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';

import { AssignSelect } from '@/ui/composites/AssignSelect';
import { TYP_ICON } from '@/modules/wissen/typIcons';
import { db } from '@/db/schema';
import { wissenLoesen, wissenVerknuepfen, type WissensVerknuepfung } from '@/db/repositories/books';
import { fortschrittAnteil, statusLabel } from '@/domain/wissen';
import { pfad } from '@/app/fokus';
import type { Book, ID } from '@/domain/types';

const INDEX = { bereich: 'areaIds', projekt: 'projectIds', ziel: 'goalIds' } as const;

export function WissenAbschnitt({ art, id, kompakt = false }: {
  art: WissensVerknuepfung;
  id: ID;
  /** An der Bereichskarte: nur Titel-Links, ohne Status. */
  kompakt?: boolean;
}) {
  const daten = useLiveQuery(async () => {
    const alle = await db.books.toArray();
    return {
      verknuepft: alle.filter((b) => (b[INDEX[art]] ?? []).includes(id)).sort((a, b) => a.title.localeCompare(b.title, 'de')),
      rest: alle.filter((b) => !(b[INDEX[art]] ?? []).includes(id) && b.status !== 'abgebrochen').sort((a, b) => a.title.localeCompare(b.title, 'de')),
    };
  }, [art, id]);
  if (!daten) return null;

  const auswahl = <AssignSelect label="Wissen verknüpfen" options={daten.rest.map((b) => ({ id: b.id, label: b.title }))}
    onAssign={(bookId) => void wissenVerknuepfen(bookId, art, id)} className="self-start" />;

  if (kompakt) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {daten.verknuepft.map((b) => <Chip key={b.id} b={b} onRemove={() => void wissenLoesen(b.id, art, id)} />)}
        {auswahl}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {daten.verknuepft.length === 0 && <p className="text-sm text-ink-faint">Noch keine Wissensquellen verknüpft.</p>}
      <ul className="flex flex-col gap-1.5">
        {daten.verknuepft.map((b) => {
          const Icon = TYP_ICON[b.typ];
          const anteil = fortschrittAnteil(b.fortschritt);
          return (
            <li key={b.id} className="group flex items-center gap-2 text-sm">
              <Icon className="size-3.5 shrink-0 text-ink-faint" aria-hidden="true" />
              <Link to={pfad.wissen(b.id)} className="min-w-0 flex-1 truncate hover:underline">{b.title}</Link>
              <span className="shrink-0 text-[11px] text-ink-faint">
                {statusLabel(b.status)}{anteil !== null && b.status === 'lese' ? ` · ${Math.round(anteil * 100)} %` : ''}
              </span>
              <button type="button" onClick={() => void wissenLoesen(b.id, art, id)} aria-label={`„${b.title}“ lösen`}
                className="grid size-6 shrink-0 place-items-center rounded text-ink-faint opacity-0 hover:text-ink focus:opacity-100 group-hover:opacity-100">
                <X className="size-3.5" />
              </button>
            </li>
          );
        })}
      </ul>
      {auswahl}
    </div>
  );
}

function Chip({ b, onRemove }: { b: Book; onRemove: () => void }) {
  const Icon = TYP_ICON[b.typ];
  return (
    <span className="inline-flex max-w-full items-center rounded-full bg-surface-raised text-[11px] text-ink-muted">
      <Link to={pfad.wissen(b.id)} title={`Wissen: ${b.title}`} className="inline-flex min-w-0 items-center gap-1 py-0.5 pl-2 pr-1.5 hover:text-ink">
        <Icon className="size-3 shrink-0" aria-hidden="true" /><span className="max-w-40 truncate">{b.title}</span>
      </Link>
      <button type="button" onClick={onRemove} aria-label={`„${b.title}“ lösen`} className="-ml-0.5 grid size-5 place-items-center rounded-full text-ink-faint hover:text-ink">
        <X className="size-3" />
      </button>
    </span>
  );
}
